/*
 * ITLabPage — workspace for the Computer Lab / IT Teacher role.
 *
 * Five tabs, one page:
 *   1. Dashboard           — KPI tiles + "recently broken" + "open procurement"
 *   2. Inventory           — devices CRUD with category / condition filters
 *   3. Repairs             — active queue (severity-sorted) + closed history
 *   4. Procurement         — wishlist with approval pipeline (admin-only decide)
 *   5. Student Requirements — items the IT teacher needs students to bring/buy
 *
 * All API calls go through the global window.fetch wrapper that injects
 * X-User-Role + X-Linked-Entity-* headers, so the backend role gate in
 * ItLabAuth.requireITLab fires automatically without manual wiring here.
 *
 * Premium UX targets per the user brief:
 *   - Gold-cream-maroon palette consistent with ClinicStudentsPage.
 *   - Animated entrance + hover for cards, rows, KPI tiles.
 *   - prefers-reduced-motion honoured throughout the CSS module.
 *   - Strict severity / status pill colours (no fallback that could
 *     hide a CRITICAL repair as "unknown" grey).
 *   - Empty states are explanatory, never blank.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./ITLabPage.module.css";

// ── Constants matched 1:1 with the backend whitelists. Drifting these
//    out of sync would let the UI offer a category the server rejects,
//    so they live here as a single source of truth.
const CATEGORIES = ["PC", "LAPTOP", "PRINTER", "PROJECTOR", "NETWORK", "PERIPHERAL", "OTHER"];
const PROCUREMENT_CATEGORIES = ["PC", "LAPTOP", "PRINTER", "PROJECTOR", "NETWORK", "PERIPHERAL", "CONSUMABLE", "OTHER"];
const CONDITIONS = ["NEW", "GOOD", "FAIR", "POOR", "BROKEN", "RETIRED"];
const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const REPAIR_STATUSES = ["REPORTED", "IN_PROGRESS", "FIXED", "RETIRED", "CANCELLED"];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

// Tabs are pure UI state (no separate routes) — same convention as
// the existing tuition / books admin pages.
const TABS = [
  { key: "dashboard",    label: "Dashboard" },
  { key: "inventory",    label: "Inventory" },
  { key: "repairs",      label: "Repairs" },
  { key: "procurement",  label: "Procurement" },
  { key: "requirements", label: "Student Requirements" },
];

// ── Tiny helpers ──────────────────────────────────────────────
const peso = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `₱${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
};

// Pill colour mappers — exhaustive switches, NEVER a default fallback
// that paints "unknown" as grey. A CRITICAL repair rendered grey would
// be the same patient-safety pattern we explicitly fixed in the clinic
// severity sort: don't hide loudness behind a fallback colour.
function severityPillClass(sev) {
  switch ((sev || "").toUpperCase()) {
    case "CRITICAL": return `${s.pill} ${s.pillCritical}`;
    case "HIGH":     return `${s.pill} ${s.pillRed}`;
    case "MEDIUM":   return `${s.pill} ${s.pillOrange}`;
    case "LOW":      return `${s.pill} ${s.pillBlue}`;
    default:         return `${s.pill} ${s.pillNeutral}`;
  }
}
function repairStatusPillClass(status) {
  switch ((status || "").toUpperCase()) {
    case "REPORTED":    return `${s.pill} ${s.pillOrange}`;
    case "IN_PROGRESS": return `${s.pill} ${s.pillBlue}`;
    case "FIXED":       return `${s.pill} ${s.pillGreen}`;
    case "RETIRED":     return `${s.pill} ${s.pillNeutral}`;
    case "CANCELLED":   return `${s.pill} ${s.pillNeutral}`;
    default:            return `${s.pill} ${s.pillNeutral}`;
  }
}
function conditionPillClass(cond) {
  switch ((cond || "").toUpperCase()) {
    case "NEW":     return `${s.pill} ${s.pillGreen}`;
    case "GOOD":    return `${s.pill} ${s.pillGreen}`;
    case "FAIR":    return `${s.pill} ${s.pillGold}`;
    case "POOR":    return `${s.pill} ${s.pillOrange}`;
    case "BROKEN":  return `${s.pill} ${s.pillRed}`;
    case "RETIRED": return `${s.pill} ${s.pillNeutral}`;
    default:        return `${s.pill} ${s.pillNeutral}`;
  }
}
function priorityPillClass(pri) {
  switch ((pri || "").toUpperCase()) {
    case "URGENT": return `${s.pill} ${s.pillCritical}`;
    case "HIGH":   return `${s.pill} ${s.pillRed}`;
    case "MEDIUM": return `${s.pill} ${s.pillOrange}`;
    case "LOW":    return `${s.pill} ${s.pillBlue}`;
    default:       return `${s.pill} ${s.pillNeutral}`;
  }
}
function procurementStatusPillClass(status) {
  switch ((status || "").toUpperCase()) {
    case "REQUESTED": return `${s.pill} ${s.pillGold}`;
    case "APPROVED":  return `${s.pill} ${s.pillGreen}`;
    case "ORDERED":   return `${s.pill} ${s.pillBlue}`;
    case "RECEIVED":  return `${s.pill} ${s.pillGreen}`;
    case "DECLINED":  return `${s.pill} ${s.pillRed}`;
    case "CANCELLED": return `${s.pill} ${s.pillNeutral}`;
    default:          return `${s.pill} ${s.pillNeutral}`;
  }
}

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

// ──────────────────────────────────────────────────────────────
//  Page shell
// ──────────────────────────────────────────────────────────────

export default function ITLabPage() {
  const user = getUser();
  const isApprover = user?.role === "Admin" || user?.role === "SuperAdmin" || user?.role === "SuperDev";

  const [tab, setTab] = useState("dashboard");

  // Top-level shared loads — devices + dashboard counts. Repairs,
  // procurement, requirements load lazily when their tab opens, then
  // cache in state so flipping back is instant.
  const [devices, setDevices]       = useState([]);
  const [dashboard, setDashboard]   = useState(null);
  const [repairs, setRepairs]       = useState(null);          // { active, closed }
  const [procurement, setProcurement] = useState(null);        // { open, closed }
  const [requirements, setRequirements] = useState(null);      // array

  // Shared lookups — loaded once at page mount so every tab/drawer
  // can render friendly labels ("Grade 5 · St. Catherine") instead of
  // raw foreign-key numbers, AND so the drawer dropdowns can offer the
  // canonical list rather than asking the user to type numeric ids.
  // Failure of any single lookup falls back to an empty list — the
  // page still renders, just with numeric fallbacks where applicable.
  const [lookups, setLookups] = useState({
    gradeLevels: [],
    sections:    [],
    rooms:       [],
    schoolYears: [],
  });

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  // Reload helpers — bound to each tab so a mutation can refresh just
  // the affected slice without forcing the whole page to refetch.
  const reloadDevices = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/itlab/devices`);
    if (r.ok) setDevices(await r.json());
  }, []);
  const reloadDashboard = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/itlab/dashboard`);
    if (r.ok) setDashboard(await r.json());
  }, []);
  const reloadRepairs = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/itlab/repairs`);
    if (r.ok) setRepairs(await r.json());
  }, []);
  const reloadProcurement = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/itlab/procurement`);
    if (r.ok) setProcurement(await r.json());
  }, []);
  const reloadRequirements = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/itlab/student-requirements`);
    if (r.ok) setRequirements(await r.json());
  }, []);

  // Initial load: devices + dashboard + the four shared lookups in
  // parallel. Other tabs (repairs / procurement / requirements) are
  // lazy-loaded on first click and cached in state.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/itlab/devices`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/itlab/dashboard`).then(r => r.ok ? r.json() : null),
      // Lookups — every endpoint already gates at its own controller.
      // Catch on each so a single 4xx doesn't abort the page load;
      // the affected dropdown just renders empty in that case.
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([d, dash, gradeLevels, sections, rooms, schoolYears]) => {
        if (cancelled) return;
        setDevices(Array.isArray(d) ? d : []);
        setDashboard(dash || null);
        setLookups({
          gradeLevels: Array.isArray(gradeLevels) ? gradeLevels : [],
          sections:    Array.isArray(sections)    ? sections    : [],
          rooms:       Array.isArray(rooms)       ? rooms       : [],
          schoolYears: Array.isArray(schoolYears) ? schoolYears : [],
        });
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message || "Failed to load"); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // Lazy-load when switching to a tab that hasn't loaded yet.
  useEffect(() => {
    if (tab === "repairs" && repairs == null) reloadRepairs();
    if (tab === "procurement" && procurement == null) reloadProcurement();
    if (tab === "requirements" && requirements == null) reloadRequirements();
  }, [tab, repairs, procurement, requirements, reloadRepairs, reloadProcurement, reloadRequirements]);

  // Device-id → device map; used by repair rows to render the device
  // label without an extra fetch per row.
  const deviceById = useMemo(() => {
    const m = new Map();
    for (const d of devices) m.set(d.id, d);
    return m;
  }, [devices]);

  return (
    <div className={s.page}>
      <Hero dashboard={dashboard} />

      <KPIRail dashboard={dashboard} />

      <div className={s.tabBar} role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`${s.tab} ${tab === t.key ? s.tabActive : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === "repairs"      && dashboard?.pendingRepairs > 0 && (
              <span className={s.tabCount}>{dashboard.pendingRepairs}</span>
            )}
            {t.key === "procurement"  && dashboard?.openProcurement > 0 && (
              <span className={s.tabCount}>{dashboard.openProcurement}</span>
            )}
            {t.key === "inventory"    && devices.length > 0 && (
              <span className={s.tabCount}>{devices.length}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className={s.errorBanner}>{error}</div>}
      {loading && <div className={s.loading}>Loading lab workspace…</div>}

      {!loading && tab === "dashboard" && (
        <DashboardTab
          dashboard={dashboard}
          deviceById={deviceById}
        />
      )}
      {!loading && tab === "inventory" && (
        <InventoryTab
          devices={devices}
          lookups={lookups}
          onChanged={async () => { await Promise.all([reloadDevices(), reloadDashboard()]); }}
        />
      )}
      {!loading && tab === "repairs" && (
        <RepairsTab
          repairs={repairs}
          deviceById={deviceById}
          devices={devices}
          onChanged={async () => { await Promise.all([reloadRepairs(), reloadDevices(), reloadDashboard()]); }}
        />
      )}
      {!loading && tab === "procurement" && (
        <ProcurementTab
          procurement={procurement}
          isApprover={isApprover}
          onChanged={async () => { await Promise.all([reloadProcurement(), reloadDashboard()]); }}
        />
      )}
      {!loading && tab === "requirements" && (
        <RequirementsTab
          requirements={requirements}
          lookups={lookups}
          onChanged={async () => { await Promise.all([reloadRequirements(), reloadDashboard()]); }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Hero + KPI rail
// ──────────────────────────────────────────────────────────────

function Hero({ dashboard }) {
  return (
    <div className={s.hero}>
      <div className={s.heroIcon}>🖥</div>
      <div className={s.heroBody}>
        <div className={s.eyebrow}>Computer Lab Workspace</div>
        <h1 className={s.heroTitle}>IT &amp; Computer Lab</h1>
        <p className={s.heroSub}>
          Track equipment inventory, log repairs and broken devices,
          plan procurement, and publish what students need to bring —
          all from one premium console.
        </p>
      </div>
    </div>
  );
}

function KPIRail({ dashboard }) {
  const total      = dashboard?.totalDevices    ?? 0;
  const working    = dashboard?.workingDevices  ?? 0;
  const broken     = dashboard?.brokenDevices   ?? 0;
  const pending    = dashboard?.pendingRepairs  ?? 0;
  const openProc   = dashboard?.openProcurement ?? 0;
  const openVal    = dashboard?.openProcurementValue ?? 0;
  const activeReq  = dashboard?.activeRequirements   ?? 0;

  return (
    <div className={s.kpiRail}>
      <KPI label="Devices" value={total} sub="Total in inventory" accent="" />
      <KPI label="Working" value={working} sub={`${broken} broken`} accent={s.kpiAccentGreen} />
      <KPI label="Pending repairs" value={pending} sub="REPORTED + IN_PROGRESS" accent={s.kpiAccentRed} />
      <KPI label="Open procurement" value={openProc} sub={`${peso(openVal)} estimated`} accent={s.kpiAccentGold} />
      <KPI label="Student requirements" value={activeReq} sub="Items on file" accent={s.kpiAccentSlate} />
    </div>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className={`${s.kpiCard} ${accent || ""}`}>
      <div className={s.kpiLabel}>{label}</div>
      <div className={s.kpiValue}>{value}</div>
      {sub && <div className={s.kpiSub}>{sub}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Dashboard tab
// ──────────────────────────────────────────────────────────────

function DashboardTab({ dashboard, deviceById }) {
  const recentRepairs   = dashboard?.recentRepairs    || [];
  const openProcurement = dashboard?.recentProcurement || [];

  return (
    <>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Recently broken / under repair</h2>
        </div>
        {recentRepairs.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🛠</div>
            <div className={s.emptyTitle}>Nothing broken right now</div>
            <div className={s.emptyText}>
              When a device is reported as broken or queued for repair,
              it surfaces here so the team can prioritise the loudest
              incident at a glance.
            </div>
          </div>
        ) : (
          <div className={s.grid}>
            {recentRepairs.map(r => {
              const dev = deviceById.get(r.deviceId);
              const isLoud = r.severity === "CRITICAL" || r.severity === "HIGH";
              return (
                <div key={r.id} className={s.gridRow}>
                  <div className={isLoud ? `${s.iconBubble} ${s.iconBubbleRed}` : s.iconBubble}>
                    {r.severity === "CRITICAL" ? "🚨" : "⚠"}
                  </div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{deviceLabel(dev) || `Device #${r.deviceId}`}</div>
                    <div className={s.rowSub}>{r.issue || "(no description)"}</div>
                    <div className={s.rowMeta}>
                      <span className={severityPillClass(r.severity)}>{r.severity}</span>
                      <span className={repairStatusPillClass(r.status)}>{(r.status || "").replace("_", " ")}</span>
                      <span className={s.rowSub}>{fmtDateTime(r.reportedAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Open procurement</h2>
        </div>
        {openProcurement.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🛒</div>
            <div className={s.emptyTitle}>No outstanding requests</div>
            <div className={s.emptyText}>
              File a procurement request from the Procurement tab when
              the lab needs new hardware, consumables, or licences.
            </div>
          </div>
        ) : (
          <div className={s.grid}>
            {openProcurement.map(p => (
              <div key={p.id} className={s.gridRow}>
                <div className={s.iconBubble}>🛒</div>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>{p.label}</div>
                  <div className={s.rowSub}>
                    Qty: {p.qty} · Est. {peso((p.estimatedUnitCost || 0) * (p.qty || 0))}
                  </div>
                  <div className={s.rowMeta}>
                    <span className={priorityPillClass(p.priority)}>{p.priority}</span>
                    <span className={procurementStatusPillClass(p.status)}>{p.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function deviceLabel(d) {
  if (!d) return "";
  const parts = [];
  if (d.assetTag) parts.push(d.assetTag);
  if (d.make)     parts.push(d.make);
  if (d.model)    parts.push(d.model);
  return parts.join(" · ");
}

// ──────────────────────────────────────────────────────────────
//  Inventory tab
// ──────────────────────────────────────────────────────────────

function InventoryTab({ devices, lookups, onChanged }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("ALL");
  const [filterCond, setFilterCond] = useState("ALL");
  const [editing, setEditing] = useState(null);   // null = closed, "new" = create, {...} = edit
  const [busy, setBusy] = useState(false);

  // Room-id → friendly label map. Pre-built once per devices render so
  // each row renders in O(1) without re-scanning the rooms list.
  const roomLabelById = useMemo(() => {
    const m = new Map();
    for (const r of lookups?.rooms || []) {
      const lbl = [r.name, r.building].filter(Boolean).join(" · ");
      m.set(r.id, lbl || `Room #${r.id}`);
    }
    return m;
  }, [lookups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter(d => {
      if (filterCat !== "ALL" && d.category !== filterCat) return false;
      if (filterCond !== "ALL" && d.condition !== filterCond) return false;
      if (!q) return true;
      const hay = [d.assetTag, d.make, d.model, d.serialNo, d.notes].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [devices, search, filterCat, filterCond]);

  const onDelete = async (d) => {
    if (!window.confirm(`Delete device ${deviceLabel(d) || ("#" + d.id)}? Repair history will also be removed.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/itlab/devices/${d.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await extractErrorMessage(r, "Delete failed"));
      await onChanged();
    } catch (e) { window.alert(e.message || "Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Equipment inventory</h2>
          <div className={s.cardActions}>
            <button className={s.btnPrimary} onClick={() => setEditing("new")}>+ Add device</button>
          </div>
        </div>

        <div className={s.toolbar}>
          <div className={s.searchBox}>
            <span aria-hidden>🔍</span>
            <input
              type="text"
              placeholder="Search by tag, make, model, serial…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button className={`${s.chip} ${filterCat === "ALL" ? s.chipActive : ""}`} onClick={() => setFilterCat("ALL")}>All categories</button>
          {CATEGORIES.map(c => (
            <button key={c} className={`${s.chip} ${filterCat === c ? s.chipActive : ""}`} onClick={() => setFilterCat(c)}>{c}</button>
          ))}
        </div>
        <div className={s.toolbar}>
          <button className={`${s.chip} ${filterCond === "ALL" ? s.chipActive : ""}`} onClick={() => setFilterCond("ALL")}>All conditions</button>
          {CONDITIONS.map(c => (
            <button key={c} className={`${s.chip} ${filterCond === c ? s.chipActive : ""}`} onClick={() => setFilterCond(c)}>{c}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🖥</div>
            <div className={s.emptyTitle}>{devices.length === 0 ? "No devices on file yet" : "No devices match the current filters"}</div>
            <div className={s.emptyText}>
              {devices.length === 0
                ? "Click “Add device” to start the inventory. Asset tag, category, and condition are the minimum to log a unit."
                : "Try clearing the search or relaxing the category / condition filter."}
            </div>
          </div>
        ) : (
          <div className={s.grid}>
            {filtered.map(d => (
              <div key={d.id} className={s.gridRow} onClick={() => setEditing(d)} role="button" tabIndex={0}>
                <div className={d.condition === "BROKEN" ? `${s.iconBubble} ${s.iconBubbleRed}` : s.iconBubble}>
                  {categoryIcon(d.category)}
                </div>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>{deviceLabel(d) || `Device #${d.id}`}</div>
                  <div className={s.rowSub}>
                    {d.serialNo ? `S/N: ${d.serialNo}` : "—"}
                    {d.unitCost ? ` · ${peso(d.unitCost)}` : ""}
                    {d.roomId ? ` · ${roomLabelById.get(d.roomId) || `Room #${d.roomId}`}` : ""}
                  </div>
                  <div className={s.rowMeta}>
                    <span className={`${s.pill} ${s.pillNeutral}`}>{d.category}</span>
                    <span className={conditionPillClass(d.condition)}>{d.condition}</span>
                  </div>
                </div>
                <div className={s.cardActions}>
                  <button className={s.btnGhost} onClick={(e) => { e.stopPropagation(); setEditing(d); }}>Edit</button>
                  <button className={s.btnGhost} onClick={(e) => { e.stopPropagation(); onDelete(d); }} disabled={busy}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <DeviceDrawer
          device={editing === "new" ? null : editing}
          lookups={lookups}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged(); }}
        />
      )}
    </>
  );
}

function categoryIcon(cat) {
  switch ((cat || "").toUpperCase()) {
    case "PC":         return "🖥";
    case "LAPTOP":     return "💻";
    case "PRINTER":    return "🖨";
    case "PROJECTOR":  return "📽";
    case "NETWORK":    return "🌐";
    case "PERIPHERAL": return "🖱";
    default:           return "📦";
  }
}

function DeviceDrawer({ device, lookups, onClose, onSaved }) {
  const isNew = !device;
  // Local form state. We seed each field from the prop and let the
  // input's onChange drive the controlled input. Empty strings are
  // converted back to null on save so the server stores absence as
  // null, not "".
  const [form, setForm] = useState(() => ({
    assetTag:          device?.assetTag          || "",
    category:          device?.category          || "PC",
    make:              device?.make              || "",
    model:             device?.model             || "",
    serialNo:          device?.serialNo          || "",
    roomId:            device?.roomId ?? "",
    condition:         device?.condition         || "GOOD",
    purchasedAt:       device?.purchasedAt       || "",
    warrantyUntil:     device?.warrantyUntil     || "",
    unitCost:          device?.unitCost ?? "",
    notes:             device?.notes             || "",
  }));
  // Photo lifecycle is separate from the form fields. The image
  // lives at a Cloudinary URL on the row — uploads + deletions hit a
  // dedicated backend endpoint that returns the updated device, which
  // we use to refresh the preview. We hold a transient currentPhoto
  // so a delete-then-cancel flow doesn't lose the still-attached URL.
  const [currentPhoto, setCurrentPhoto] = useState(device?.photoUrl || null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.category) { setError("Category is required."); return; }
    if (!form.condition) { setError("Condition is required."); return; }
    setBusy(true);
    try {
      const body = {
        assetTag:      form.assetTag.trim() || null,
        category:      form.category,
        make:          form.make.trim() || null,
        model:         form.model.trim() || null,
        serialNo:      form.serialNo.trim() || null,
        // roomId comes from the dropdown as a numeric string; "" means
        // unassigned / null, anything else is parsed to a Number so the
        // server reads it as a Long.
        roomId:        form.roomId === "" ? null : Number(form.roomId),
        condition:     form.condition,
        purchasedAt:   form.purchasedAt || null,
        warrantyUntil: form.warrantyUntil || null,
        unitCost:      form.unitCost === "" ? null : Number(form.unitCost),
        // photoUrl is no longer free-form on the client. It's owned by
        // the upload endpoint, so we send the current value untouched.
        photoUrl:      currentPhoto || null,
        notes:         form.notes.trim() || null,
      };
      const url = isNew
        ? `${API_BASE}/api/itlab/devices`
        : `${API_BASE}/api/itlab/devices/${device.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Save failed (${r.status})`));
      }
      await onSaved();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally { setBusy(false); }
  };

  // Upload a picked file directly to /api/itlab/devices/{id}/photo.
  // Disabled for new (unsaved) devices because we need an id to write
  // against — the user is asked to save the basic details first.
  const onPickPhoto = async (file) => {
    if (!file) return;
    setPhotoError("");
    if (isNew) {
      setPhotoError("Save the device first, then upload a photo.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setPhotoError("Pick an image file (jpg / png / webp).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError("Image exceeds the 10 MB cap.");
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_BASE}/api/itlab/devices/${device.id}/photo`, {
        method: "POST",
        body: fd,
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Upload failed (${r.status})`));
      const updated = await r.json();
      setCurrentPhoto(updated.photoUrl || null);
    } catch (e) {
      setPhotoError(e.message || "Upload failed");
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = async () => {
    if (isNew || !currentPhoto) {
      setCurrentPhoto(null);
      return;
    }
    if (!window.confirm("Remove this device photo?")) return;
    setPhotoUploading(true);
    setPhotoError("");
    try {
      const r = await fetch(`${API_BASE}/api/itlab/devices/${device.id}/photo`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Remove failed (${r.status})`));
      setCurrentPhoto(null);
    } catch (e) {
      setPhotoError(e.message || "Remove failed");
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <div className={s.drawerBackdrop}>
      <div className={s.drawer} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>{isNew ? "Add device" : "Edit device"}</h3>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.formGrid}>
          {/* Photo zone spans the full width at the top — the visual
              anchor for a device record. Preview tile + file picker
              + remove control. Disabled for unsaved devices because
              the upload endpoint needs a device id to write against. */}
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Photo</label>
            <PhotoPicker
              photoUrl={currentPhoto}
              uploading={photoUploading}
              error={photoError}
              disabled={isNew}
              disabledHint="Save the device first, then upload a photo."
              onPick={onPickPhoto}
              onRemove={removePhoto}
            />
          </div>

          <div className={s.formField}>
            <label className={s.formLabel}>Asset tag</label>
            <input className={s.formInput} value={form.assetTag} onChange={set("assetTag")} placeholder="PC-014" />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Category *</label>
            <select className={s.formSelect} value={form.category} onChange={set("category")}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Make</label>
            <input className={s.formInput} value={form.make} onChange={set("make")} placeholder="HP, Dell, Epson…" />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Model</label>
            <input className={s.formInput} value={form.model} onChange={set("model")} placeholder="EliteDesk 800 G6" />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Serial number</label>
            <input className={s.formInput} value={form.serialNo} onChange={set("serialNo")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Room</label>
            <select className={s.formSelect} value={form.roomId} onChange={set("roomId")}>
              <option value="">— Not assigned —</option>
              {(lookups?.rooms || []).map(r => (
                <option key={r.id} value={r.id}>
                  {[r.name, r.building].filter(Boolean).join(" · ") || `Room #${r.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Condition *</label>
            <select className={s.formSelect} value={form.condition} onChange={set("condition")}>
              {CONDITIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Purchased</label>
            <input className={s.formInput} type="date" value={form.purchasedAt} onChange={set("purchasedAt")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Warranty until</label>
            <input className={s.formInput} type="date" value={form.warrantyUntil} onChange={set("warrantyUntil")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Unit cost (₱)</label>
            <input className={s.formInput} type="number" step="0.01" min="0" value={form.unitCost} onChange={set("unitCost")} />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Notes</label>
            <textarea className={s.formTextarea} value={form.notes} onChange={set("notes")} />
          </div>
          {error && <div className={s.formError}>{error}</div>}
          <div className={s.formActions}>
            <button className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={save} disabled={busy}>{busy ? "Saving…" : (isNew ? "Add device" : "Save changes")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Photo picker for the DeviceDrawer. Renders a square preview tile
 * (or a dashed placeholder when there's no photo yet), a "Choose
 * file" CTA, and a "Remove" affordance when a photo is attached.
 *
 * The upload itself lives on the parent so it can deal with the
 * Cloudinary round-trip and the form state in one place; this
 * component is purely presentational + a file-input proxy. */
function PhotoPicker({ photoUrl, uploading, error, disabled, disabledHint, onPick, onRemove }) {
  const fileInputRef = useRef(null);
  const openPicker = () => fileInputRef.current && fileInputRef.current.click();
  return (
    <div className={s.photoPicker}>
      <div className={s.photoPreview}>
        {photoUrl
          ? <img src={photoUrl} alt="Device" className={s.photoPreviewImg} />
          : <div className={s.photoPlaceholder} aria-hidden>🖥</div>}
      </div>
      <div className={s.photoActions}>
        <button
          type="button"
          className={s.btnSecondary}
          onClick={openPicker}
          disabled={disabled || uploading}
        >
          {uploading ? "Uploading…" : photoUrl ? "Replace photo" : "Choose photo"}
        </button>
        {photoUrl && (
          <button
            type="button"
            className={`${s.btnGhost} ${s.btnDangerText}`}
            onClick={onRemove}
            disabled={uploading}
          >
            Remove
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            // Reset the input so picking the same file twice still
            // fires onChange — otherwise the second pick is a no-op.
            e.target.value = "";
            if (f) onPick(f);
          }}
        />
        {disabled && disabledHint && (
          <div className={s.photoHint}>{disabledHint}</div>
        )}
        {error && <div className={s.photoError}>{error}</div>}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Repairs tab
// ──────────────────────────────────────────────────────────────

function RepairsTab({ repairs, deviceById, devices, onChanged }) {
  const [showClosed, setShowClosed] = useState(false);
  const [editing, setEditing] = useState(null);

  const active = repairs?.active || [];
  const closed = repairs?.closed || [];

  return (
    <>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Active repairs</h2>
          <div className={s.cardActions}>
            <button className={s.btnPrimary} onClick={() => setEditing("new")} disabled={devices.length === 0}>
              + File a repair
            </button>
          </div>
        </div>
        {devices.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🖥</div>
            <div className={s.emptyTitle}>No devices yet</div>
            <div className={s.emptyText}>Add a device under the Inventory tab first — a repair always points at a specific device.</div>
          </div>
        ) : active.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🛠</div>
            <div className={s.emptyTitle}>All clear</div>
            <div className={s.emptyText}>No active repair tickets. File one above when a device starts misbehaving.</div>
          </div>
        ) : (
          <div className={s.grid}>
            {active.map(r => {
              const dev = deviceById.get(r.deviceId);
              const isLoud = r.severity === "CRITICAL" || r.severity === "HIGH";
              return (
                <div key={r.id} className={s.gridRow} role="button" tabIndex={0} onClick={() => setEditing(r)}>
                  <div className={isLoud ? `${s.iconBubble} ${s.iconBubbleRed}` : s.iconBubble}>
                    {r.severity === "CRITICAL" ? "🚨" : "⚠"}
                  </div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{deviceLabel(dev) || `Device #${r.deviceId}`}</div>
                    <div className={s.rowSub}>{r.issue}</div>
                    <div className={s.rowMeta}>
                      <span className={severityPillClass(r.severity)}>{r.severity}</span>
                      <span className={repairStatusPillClass(r.status)}>{(r.status || "").replace("_", " ")}</span>
                      <span className={s.rowSub}>{fmtDateTime(r.reportedAt)} · {r.reportedByUsername || "system"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Closed history</h2>
          <button className={s.btnGhost} onClick={() => setShowClosed(v => !v)}>
            {showClosed ? "Hide" : `Show (${closed.length})`}
          </button>
        </div>
        {showClosed && (
          closed.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}>📜</div>
              <div className={s.emptyTitle}>No closed repairs yet</div>
              <div className={s.emptyText}>FIXED, RETIRED, and CANCELLED repair tickets land here for the record.</div>
            </div>
          ) : (
            <div className={s.grid}>
              {closed.map(r => {
                const dev = deviceById.get(r.deviceId);
                return (
                  <div key={r.id} className={s.gridRow} role="button" tabIndex={0} onClick={() => setEditing(r)}>
                    <div className={s.iconBubble}>{r.status === "FIXED" ? "🛠" : r.status === "RETIRED" ? "🪦" : "✖"}</div>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{deviceLabel(dev) || `Device #${r.deviceId}`}</div>
                      <div className={s.rowSub}>{r.resolutionNotes || r.issue}</div>
                      <div className={s.rowMeta}>
                        <span className={repairStatusPillClass(r.status)}>{r.status}</span>
                        <span className={s.rowSub}>{fmtDateTime(r.resolvedAt || r.reportedAt)}</span>
                        {r.repairCost ? <span className={s.rowSub}>· {peso(r.repairCost)}</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {editing && (
        <RepairDrawer
          repair={editing === "new" ? null : editing}
          devices={devices}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged(); }}
        />
      )}
    </>
  );
}

function RepairDrawer({ repair, devices, onClose, onSaved }) {
  const isNew = !repair;
  const [form, setForm] = useState(() => ({
    deviceId:         repair?.deviceId         ?? (devices[0]?.id ?? ""),
    issue:            repair?.issue            || "",
    severity:         repair?.severity         || "MEDIUM",
    status:           repair?.status           || "REPORTED",
    repairCost:       repair?.repairCost ?? "",
    resolutionNotes:  repair?.resolutionNotes  || "",
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.deviceId) { setError("Device is required."); return; }
    if (!form.issue.trim()) { setError("Issue description is required."); return; }
    setBusy(true);
    try {
      const body = {
        deviceId:        Number(form.deviceId),
        issue:           form.issue.trim(),
        severity:        form.severity,
        status:          form.status,
        repairCost:      form.repairCost === "" ? null : Number(form.repairCost),
        resolutionNotes: form.resolutionNotes.trim() || null,
      };
      const url = isNew
        ? `${API_BASE}/api/itlab/repairs`
        : `${API_BASE}/api/itlab/repairs/${repair.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Save failed (${r.status})`));
      }
      await onSaved();
    } catch (e) { setError(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={s.drawerBackdrop}>
      <div className={s.drawer} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>{isNew ? "File a repair" : "Update repair"}</h3>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.formGrid}>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Device *</label>
            <select className={s.formSelect} value={form.deviceId} onChange={set("deviceId")} disabled={!isNew}>
              {devices.map(d => (
                <option key={d.id} value={d.id}>{deviceLabel(d) || `Device #${d.id}`}</option>
              ))}
            </select>
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Issue *</label>
            <textarea className={s.formTextarea} value={form.issue} onChange={set("issue")} placeholder="Describe the symptoms…" />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Severity</label>
            <select className={s.formSelect} value={form.severity} onChange={set("severity")}>
              {SEVERITIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Status</label>
            <select className={s.formSelect} value={form.status} onChange={set("status")}>
              {REPAIR_STATUSES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Repair cost (₱)</label>
            <input className={s.formInput} type="number" step="0.01" min="0" value={form.repairCost} onChange={set("repairCost")} />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Resolution notes</label>
            <textarea className={s.formTextarea} value={form.resolutionNotes} onChange={set("resolutionNotes")} placeholder="What was done? Parts replaced?" />
          </div>
          {error && <div className={s.formError}>{error}</div>}
          <div className={s.formActions}>
            <button className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={save} disabled={busy}>{busy ? "Saving…" : (isNew ? "File repair" : "Save changes")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Procurement tab
// ──────────────────────────────────────────────────────────────

function ProcurementTab({ procurement, isApprover, onChanged }) {
  const [showClosed, setShowClosed] = useState(false);
  const [editing, setEditing] = useState(null);
  const open = procurement?.open || [];
  const closed = procurement?.closed || [];

  const decide = async (req, action, notes) => {
    try {
      const r = await fetch(`${API_BASE}/api/itlab/procurement/${req.id}/decision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, notes: notes || null }),
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Action ${action} failed`));
      await onChanged();
    } catch (e) { window.alert(e.message || "Action failed"); }
  };

  const cancel = async (req) => {
    if (!window.confirm(`Cancel "${req.label}"?`)) return;
    try {
      const r = await fetch(`${API_BASE}/api/itlab/procurement/${req.id}/cancel`, { method: "POST" });
      if (!r.ok) throw new Error(await extractErrorMessage(r, "Cancel failed"));
      await onChanged();
    } catch (e) { window.alert(e.message || "Cancel failed"); }
  };

  return (
    <>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Open requests</h2>
          <div className={s.cardActions}>
            <button className={s.btnPrimary} onClick={() => setEditing("new")}>+ New request</button>
          </div>
        </div>
        {open.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🛒</div>
            <div className={s.emptyTitle}>No open procurement</div>
            <div className={s.emptyText}>
              Use this list to ask Admin for replacement parts, new
              equipment, or consumables. Approval goes through
              Admin / SuperAdmin; you'll get a bell notification when
              they decide.
            </div>
          </div>
        ) : (
          <div className={s.grid}>
            {open.map(p => (
              <div key={p.id} className={s.gridRow}>
                <div className={s.iconBubble}>🛒</div>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>{p.label}</div>
                  <div className={s.rowSub}>
                    Qty: {p.qty} · Est. {peso((p.estimatedUnitCost || 0) * (p.qty || 0))}
                    {p.requestedByUsername ? ` · by ${p.requestedByUsername}` : ""}
                  </div>
                  <div className={s.rowMeta}>
                    <span className={priorityPillClass(p.priority)}>{p.priority}</span>
                    <span className={procurementStatusPillClass(p.status)}>{p.status}</span>
                    {p.category ? <span className={`${s.pill} ${s.pillNeutral}`}>{p.category}</span> : null}
                  </div>
                </div>
                <div className={s.cardActions}>
                  {p.status === "REQUESTED" && isApprover && (
                    <>
                      <button className={s.btnSecondary} onClick={() => decide(p, "APPROVE")}>Approve</button>
                      <button className={`${s.btnSecondary} ${s.btnDanger}`} onClick={() => {
                        const notes = window.prompt("Reason for decline (optional):");
                        decide(p, "DECLINE", notes);
                      }}>Decline</button>
                    </>
                  )}
                  {p.status === "APPROVED" && isApprover && (
                    <button className={s.btnSecondary} onClick={() => decide(p, "ORDER")}>Mark ordered</button>
                  )}
                  {p.status === "ORDERED" && isApprover && (
                    <button className={s.btnSecondary} onClick={() => decide(p, "RECEIVE")}>Mark received</button>
                  )}
                  {p.status === "REQUESTED" && (
                    <button className={s.btnGhost} onClick={() => setEditing(p)}>Edit</button>
                  )}
                  <button className={s.btnGhost} onClick={() => cancel(p)}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Decision history</h2>
          <button className={s.btnGhost} onClick={() => setShowClosed(v => !v)}>
            {showClosed ? "Hide" : `Show (${closed.length})`}
          </button>
        </div>
        {showClosed && (
          closed.length === 0 ? (
            <div className={s.emptyState}>
              <div className={s.emptyIcon}>📜</div>
              <div className={s.emptyTitle}>No closed requests yet</div>
              <div className={s.emptyText}>RECEIVED, DECLINED, and CANCELLED requests land here.</div>
            </div>
          ) : (
            <div className={s.grid}>
              {closed.map(p => (
                <div key={p.id} className={s.gridRow}>
                  <div className={s.iconBubble}>{p.status === "DECLINED" ? "✖" : p.status === "RECEIVED" ? "📦" : "🛒"}</div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{p.label}</div>
                    <div className={s.rowSub}>
                      {p.decisionNotes || "(no notes)"} · {fmtDateTime(p.decidedAt || p.requestedAt)}
                      {p.decidedByUsername ? ` · by ${p.decidedByUsername}` : ""}
                    </div>
                    <div className={s.rowMeta}>
                      <span className={procurementStatusPillClass(p.status)}>{p.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {editing && (
        <ProcurementDrawer
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged(); }}
        />
      )}
    </>
  );
}

function ProcurementDrawer({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState(() => ({
    label:               item?.label              || "",
    category:            item?.category           || "",
    qty:                 item?.qty                ?? 1,
    estimatedUnitCost:   item?.estimatedUnitCost  ?? "",
    justification:       item?.justification      || "",
    priority:            item?.priority           || "MEDIUM",
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    if (!form.label.trim()) { setError("Label is required."); return; }
    const qty = Number(form.qty);
    if (!Number.isFinite(qty) || qty <= 0) { setError("Quantity must be a positive integer."); return; }
    setBusy(true);
    try {
      const body = {
        label:             form.label.trim(),
        category:          form.category || null,
        qty:               Math.max(1, Math.floor(qty)),
        estimatedUnitCost: form.estimatedUnitCost === "" ? null : Number(form.estimatedUnitCost),
        justification:     form.justification.trim() || null,
        priority:          form.priority,
      };
      const url = isNew
        ? `${API_BASE}/api/itlab/procurement`
        : `${API_BASE}/api/itlab/procurement/${item.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Save failed (${r.status})`));
      }
      await onSaved();
    } catch (e) { setError(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={s.drawerBackdrop}>
      <div className={s.drawer} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>{isNew ? "New procurement request" : "Edit request"}</h3>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.formGrid}>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Item *</label>
            <input className={s.formInput} value={form.label} onChange={set("label")} placeholder="5× HDMI cables, replacement projector bulb…" />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Category</label>
            <select className={s.formSelect} value={form.category} onChange={set("category")}>
              <option value="">—</option>
              {PROCUREMENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Priority</label>
            <select className={s.formSelect} value={form.priority} onChange={set("priority")}>
              {PRIORITIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Quantity *</label>
            <input className={s.formInput} type="number" min="1" step="1" value={form.qty} onChange={set("qty")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Est. unit cost (₱)</label>
            <input className={s.formInput} type="number" step="0.01" min="0" value={form.estimatedUnitCost} onChange={set("estimatedUnitCost")} />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Justification</label>
            <textarea className={s.formTextarea} value={form.justification} onChange={set("justification")} placeholder="Why does the lab need this?" />
          </div>
          {error && <div className={s.formError}>{error}</div>}
          <div className={s.formActions}>
            <button className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={save} disabled={busy}>{busy ? "Saving…" : (isNew ? "Submit request" : "Save changes")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Student Requirements tab
// ──────────────────────────────────────────────────────────────

function RequirementsTab({ requirements, lookups, onChanged }) {
  const [editing, setEditing] = useState(null);
  const rows = requirements || [];

  // id-keyed lookup maps so every row can render the friendly grade
  // / section / school-year label in O(1) — pre-built once per
  // lookups change. Falls back to "Grade #5" / "Section #2" if a row
  // points at a non-existent id (legacy data).
  const gradeById = useMemo(() => {
    const m = new Map();
    for (const g of lookups?.gradeLevels || []) m.set(g.id, g);
    return m;
  }, [lookups]);
  const sectionById = useMemo(() => {
    const m = new Map();
    for (const sec of lookups?.sections || []) m.set(sec.id, sec);
    return m;
  }, [lookups]);
  const syById = useMemo(() => {
    const m = new Map();
    for (const y of lookups?.schoolYears || []) m.set(y.id, y);
    return m;
  }, [lookups]);

  const audienceLabel = (r) => {
    if (r.sectionId) {
      const sec = sectionById.get(r.sectionId);
      const g = sec ? gradeById.get(sec.gradeLevelId) : null;
      if (sec && g) return `${g.name} · ${sec.name}`;
      if (sec) return sec.name;
      return `Section #${r.sectionId}`;
    }
    if (r.gradeLevelId) {
      const g = gradeById.get(r.gradeLevelId);
      return g ? g.name : `Grade #${r.gradeLevelId}`;
    }
    return "School-wide";
  };
  const syLabel = (r) => {
    const y = syById.get(r.schoolYearId);
    return y ? y.label : (r.schoolYearId ? `SY #${r.schoolYearId}` : "");
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove requirement "${r.label}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/itlab/student-requirements/${r.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Delete failed"));
      await onChanged();
    } catch (e) { window.alert(e.message || "Delete failed"); }
  };

  return (
    <>
      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>Items students must bring / buy</h2>
          <div className={s.cardActions}>
            <button className={s.btnPrimary} onClick={() => setEditing("new")}>+ New requirement</button>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>📋</div>
            <div className={s.emptyTitle}>Nothing required yet</div>
            <div className={s.emptyText}>
              Publish items here (flash drives, headphones, lab gowns…)
              and they show up on the dashboards of the students they
              apply to — and their parents.
            </div>
          </div>
        ) : (
          <div className={s.grid}>
            {rows.map(r => (
              <div key={r.id} className={s.gridRow} role="button" tabIndex={0} onClick={() => setEditing(r)}>
                <div className={r.mandatory ? `${s.iconBubble} ${s.iconBubbleRed}` : s.iconBubble}>📋</div>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>{r.label}</div>
                  <div className={s.rowSub}>
                    {r.description || "—"}
                    {r.dueDate ? ` · Due ${fmtDate(r.dueDate)}` : ""}
                    {r.estimatedCost ? ` · Est. ${peso(r.estimatedCost)}` : ""}
                  </div>
                  <div className={s.rowMeta}>
                    {r.mandatory
                      ? <span className={`${s.pill} ${s.pillRed}`}>Mandatory</span>
                      : <span className={`${s.pill} ${s.pillNeutral}`}>Optional</span>}
                    <span className={`${s.pill} ${r.sectionId ? s.pillBlue : r.gradeLevelId ? s.pillGold : s.pillNeutral}`}>
                      {audienceLabel(r)}
                    </span>
                    {r.schoolYearId && (
                      <span className={`${s.pill} ${s.pillNeutral}`}>{syLabel(r)}</span>
                    )}
                  </div>
                </div>
                <div className={s.cardActions}>
                  <button className={s.btnGhost} onClick={(e) => { e.stopPropagation(); remove(r); }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <RequirementDrawer
          item={editing === "new" ? null : editing}
          lookups={lookups}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged(); }}
        />
      )}
    </>
  );
}

function RequirementDrawer({ item, lookups, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState(() => ({
    label:         item?.label         || "",
    description:   item?.description   || "",
    gradeLevelId:  item?.gradeLevelId  ?? "",
    sectionId:     item?.sectionId     ?? "",
    schoolYearId:  item?.schoolYearId  ?? "",
    dueDate:       item?.dueDate       || "",
    mandatory:     item?.mandatory     ?? true,
    estimatedCost: item?.estimatedCost ?? "",
    notes:         item?.notes         || "",
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Default the school year to the currently-active one as soon as
  // lookups arrive AND the form hasn't already pinned a value. Mirrors
  // the SOA / Tuition convention so the IT teacher doesn't have to
  // re-type the active SY on every requirement they publish.
  useEffect(() => {
    if (form.schoolYearId) return;
    const active = (lookups?.schoolYears || []).find(sy => sy.active);
    if (active?.id) {
      setForm(f => f.schoolYearId ? f : { ...f, schoolYearId: active.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookups]);

  // Section dropdown narrows by the picked grade level so the teacher
  // can't accidentally publish a Section A-Grade-7 requirement onto
  // Section A-Grade-5 (which would actually map to a different row).
  // When no grade is picked, all sections are shown — caller can still
  // narrow manually. Sections without a gradeLevelId pass through.
  const sectionsForGrade = useMemo(() => {
    const allSections = lookups?.sections || [];
    if (!form.gradeLevelId) return allSections;
    const gid = Number(form.gradeLevelId);
    return allSections.filter(sec =>
      sec.gradeLevelId == null || sec.gradeLevelId === gid
    );
  }, [lookups, form.gradeLevelId]);

  const set = (k) => (e) => setForm(f => {
    const next = {
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    };
    // Changing the grade level invalidates the currently-picked
    // section if it doesn't belong to the new grade. Clearing it
    // beats silently submitting a mismatched pair.
    if (k === "gradeLevelId" && next.sectionId) {
      const gid = next.gradeLevelId === "" ? null : Number(next.gradeLevelId);
      const sec = (lookups?.sections || []).find(x => x.id === Number(next.sectionId));
      if (sec && gid != null && sec.gradeLevelId != null && sec.gradeLevelId !== gid) {
        next.sectionId = "";
      }
    }
    return next;
  });

  const save = async () => {
    setError("");
    if (!form.label.trim()) { setError("Label is required."); return; }
    if (!form.schoolYearId) { setError("School year is required."); return; }
    setBusy(true);
    try {
      const body = {
        label:         form.label.trim(),
        description:   form.description.trim() || null,
        gradeLevelId:  form.gradeLevelId  === "" ? null : Number(form.gradeLevelId),
        sectionId:     form.sectionId     === "" ? null : Number(form.sectionId),
        schoolYearId:  Number(form.schoolYearId),
        dueDate:       form.dueDate || null,
        mandatory:     !!form.mandatory,
        estimatedCost: form.estimatedCost === "" ? null : Number(form.estimatedCost),
        notes:         form.notes.trim() || null,
      };
      const url = isNew
        ? `${API_BASE}/api/itlab/student-requirements`
        : `${API_BASE}/api/itlab/student-requirements/${item.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Save failed (${r.status})`));
      }
      await onSaved();
    } catch (e) { setError(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className={s.drawerBackdrop}>
      <div className={s.drawer} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>{isNew ? "Publish requirement" : "Edit requirement"}</h3>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.formGrid}>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Item *</label>
            <input className={s.formInput} value={form.label} onChange={set("label")} placeholder="USB flash drive, 16GB or larger" />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Description</label>
            <textarea className={s.formTextarea} value={form.description} onChange={set("description")} placeholder="Brand suggestions, where to buy, etc." />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Grade level</label>
            <select className={s.formSelect} value={form.gradeLevelId} onChange={set("gradeLevelId")}>
              <option value="">— All grades —</option>
              {(lookups?.gradeLevels || []).map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Section</label>
            <select className={s.formSelect} value={form.sectionId} onChange={set("sectionId")}>
              <option value="">— All sections in grade —</option>
              {sectionsForGrade.map(sec => {
                const g = (lookups?.gradeLevels || []).find(x => x.id === sec.gradeLevelId);
                return (
                  <option key={sec.id} value={sec.id}>
                    {g ? `${g.name} · ${sec.name}` : sec.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>School year *</label>
            <select className={s.formSelect} value={form.schoolYearId} onChange={set("schoolYearId")}>
              <option value="">— Pick a school year —</option>
              {(lookups?.schoolYears || []).map(y => (
                <option key={y.id} value={y.id}>
                  {y.label}{y.active ? " (active)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Due date</label>
            <input className={s.formInput} type="date" value={form.dueDate} onChange={set("dueDate")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Est. cost (₱)</label>
            <input className={s.formInput} type="number" step="0.01" min="0" value={form.estimatedCost} onChange={set("estimatedCost")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>&nbsp;</label>
            <label className={s.formCheckbox}>
              <input type="checkbox" checked={!!form.mandatory} onChange={set("mandatory")} />
              Mandatory
            </label>
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Notes</label>
            <textarea className={s.formTextarea} value={form.notes} onChange={set("notes")} />
          </div>
          {error && <div className={s.formError}>{error}</div>}
          <div className={s.formActions}>
            <button className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={save} disabled={busy}>{busy ? "Saving…" : (isNew ? "Publish" : "Save changes")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
